import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from "lucide-react";

/**
 * Premium calendar popover for the Appointments page.
 *   - Month view with prev/next month & year (single and double chevrons)
 *   - Highlights today + selected date
 *   - Small dot indicators for dates that have appointments
 *   - "Today" and "Jump to" quick actions
 *   - Closes on outside click / Escape
 *
 * Props:
 *   value: ISO date string 'YYYY-MM-DD'
 *   onChange: (isoDate) => void   — called on day pick (popover closes automatically)
 *   onClose: () => void
 *   markedDates: Set<string>       — dates that have appointments (for dot indicators)
 *   anchorRef: ref to the button that opens this popover (for outside-click detection)
 */
export function CalendarPopover({ value, onChange, onClose, markedDates, anchorRef }) {
  const initial = value ? new Date(value + "T00:00:00") : new Date();
  const [cursor, setCursor] = useState(() => new Date(initial.getFullYear(), initial.getMonth(), 1));
  const popRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    const handleEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose, anchorRef]);

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const today = new Date();
  const todayISO =
      today.getFullYear() +
      "-" +
      String(today.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(today.getDate()).padStart(2, "0");

  const step = (dir, unit) => {
    const d = new Date(cursor);
    if (unit === "year") d.setFullYear(d.getFullYear() + dir);
    else d.setMonth(d.getMonth() + dir);
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={popRef}
        initial={{ opacity: 0, y: -6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -6, scale: 0.98 }}
        transition={{ duration: 0.18, ease: [0.2, 0.7, 0.2, 1] }}
        className="absolute top-full right-0 mt-2 z-40 w-[230px] p-3 rounded-xl shadow-[0_16px_40px_-12px_rgba(2,32,32,0.30)] border"
        style={{
          background:
            "linear-gradient(180deg,#0F3B3B 0%,#0C3132 100%)",
          borderColor: "rgba(243,197,90,0.18)",
        }}
        data-testid="apt-calendar-popover"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <IconBtn onClick={() => step(-1, "year")}  ariaLabel="Previous year"><ChevronsLeft className="w-4 h-4" strokeWidth={1.5} /></IconBtn>
            <IconBtn onClick={() => step(-1, "month")} ariaLabel="Previous month"><ChevronLeft className="w-4 h-4" strokeWidth={1.5} /></IconBtn>
          </div>
          <div className="text-[10px] font-medium text-white tracking-tight" data-testid="apt-calendar-month">{monthLabel}</div>
          <div className="flex items-center gap-1">
            <IconBtn onClick={() => step(1, "month")} ariaLabel="Next month"><ChevronRight className="w-4 h-4" strokeWidth={1.5} /></IconBtn>
            <IconBtn onClick={() => step(1, "year")}  ariaLabel="Next year"><ChevronsRight className="w-4 h-4" strokeWidth={1.5} /></IconBtn>
          </div>
        </div>

        {/* Weekdays */}
        <div className="grid grid-cols-7 mb-1 text-[8px] tracking-[0.12em] uppercase text-white/45 font-medium">
          {["S","M","T","W","T","F","S"].map((d, i) => (
            <div key={i} className="h-5 flex items-center justify-center">{d}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-y-0.5">
          {grid.map((cell, i) => {
            if (!cell) return <div key={i} className="h-9" />;
            const iso = cell.iso;
            const isToday = iso === todayISO;
            const isSelected = iso === value;
            const hasAppt = markedDates?.has(iso);
            return (
              <button
                key={iso}
                onClick={() => { onChange(iso); onClose(); }}
                data-testid={`apt-calendar-day-${iso}`}
                aria-pressed={isSelected}
                className="relative h-7 w-7 mx-auto rounded-md flex items-center justify-center text-[10px] transition-all group"
                style={
                  isSelected
                    ? { background: "#F3C55A", color: "#0F2D31",   fontWeight: 500, fontSize: "11px", }
                    : isToday
                    ? { background: "rgba(243,197,90,0.10)", color: "#F3C55A", fontWeight: 500 }
                    : { color: "rgba(255,255,255,0.82)" }
                }
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={(e) => {
                  if (isSelected) return;
                  e.currentTarget.style.background = isToday ? "rgba(243,197,90,0.10)" : "transparent";
                }}
              >
                {cell.day}
                {hasAppt && !isSelected && (
                  <span
                    className="absolute bottom-1 w-0.5 h-0.5 rounded-full"
                    style={{ background: isToday ? "#F3C55A" : "#2FCBA9" }}
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
       <div className="mt-1.5 pt-2 border-t border-white/10 flex items-center justify-between text-[11px]">
          <button
            onClick={() => { onChange(todayISO); onClose(); }}
            className="px-2.5 h-6 rounded-full text-white/85 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="apt-calendar-today"
          >
            Today
          </button>
          <button
            onClick={onClose}
            className="text-[11px] text-white/50 hover:text-white/80 transition-colors flex items-center gap-1"
            aria-label="Close"
          >
            <X className="w-2.5 h-2.5" /> Close
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function IconBtn({ children, onClick, ariaLabel }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="w-6 h-6 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 transition-colors"
    >
      {children}
    </button>
  );
}

/** Build a 6-row calendar grid for the given month (nulls for leading/trailing blanks). */
function buildGrid(cursor) {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(y, m, d);
    const iso =
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0");
    cells.push({ day: d, iso });
  }
  const totalCells = Math.ceil(cells.length / 7) * 7;
  while (cells.length < totalCells) cells.push(null);
  return cells;
}
